import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Section, TextField, TextArea, Toggle, AdminButton } from "@/components/admin/AdminUI";
import MediaUploader from "@/components/admin/MediaUploader";
import MultiMediaUploader from "@/components/admin/MultiMediaUploader";
import { useSettings } from "@/contexts/SettingsContext";
import { GripVertical } from "lucide-react";

const SECTION_LABELS = { hero: "Hero", photofeed: "Photo Feed", weddings: "Featured Weddings", reels: "Reels", moodboards: "Moodboards", stories: "Stories", about: "About" };

export default function SettingsAdmin() {
  const { settings, reload } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { if (settings) setForm(JSON.parse(JSON.stringify(settings))); }, [settings]);

  if (!form) return <AdminLayout><div>Loading…</div></AdminLayout>;

  const save = async () => {
    setSaving(true); setMsg(""); setErr("");
    try {
      await api.put("/settings", form);
      await reload();
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const moveSection = (idx, dir) => {
    const arr = [...form.section_order];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setForm({ ...form, section_order: arr });
  };

  const setSocials = (k, v) => setForm({ ...form, socials: { ...form.socials, [k]: v } });
  const setColor = (k, v) => setForm({ ...form, colors: { ...form.colors, [k]: v } });

  return (
    <AdminLayout>
      <Section title="Site Settings" actions={
        <div className="flex gap-3 items-center">
          {msg && <span className="text-[color:var(--accent)] text-sm">{msg}</span>}
          <AdminButton testId="settings-save" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save all"}</AdminButton>
        </div>
      }>
        {err && <div className="text-red-400 text-sm mb-4">{err}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-5">
            <h3 className="eyebrow">Brand</h3>
            <TextField testId="s-brand" label="Brand name" value={form.brand_name} onChange={(v) => setForm({ ...form, brand_name: v })} />
            <div>
              <div className="eyebrow mb-2">Logo</div>
              <MediaUploader
                testId="s-logo-upload"
                value={{ url: form.logo_url, public_id: form.logo_public_id, storage_key: form.logo_storage_key }}
                onChange={(v) => setForm({ ...form, logo_url: v.url, logo_public_id: v.public_id, logo_storage_key: v.storage_key })}
                folder="logos" purpose="logo" label="Upload logo"
              />
            </div>
            <TextField testId="s-tagline" label="Hero tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
            <TextArea testId="s-subtagline" label="Hero subtext" value={form.subtagline} onChange={(v) => setForm({ ...form, subtagline: v })} rows={2} />
            <div>
              <div className="eyebrow mb-2">Hero media (background/collage)</div>
              <MultiMediaUploader
                testId="s-hero-upload"
                items={(form.hero_media || []).map((m) => ({ url: m.url, public_id: m.public_id, storage_key: m.storage_key, provider: m.provider, media_id: m.media_id, type: "image" }))}
                onChange={(items) => setForm({ ...form, hero_media: items.map((i) => ({ url: i.url, public_id: i.public_id, storage_key: i.storage_key, provider: i.provider })) })}
                folder="homepage" purpose="hero" resourceType="image"
              />
            </div>
          </div>

          <div className="space-y-5">
            <h3 className="eyebrow">About</h3>
            <TextField testId="s-about-heading" label="About heading" value={form.about_heading} onChange={(v) => setForm({ ...form, about_heading: v })} />
            <TextArea testId="s-about-body" label="About body" value={form.about_body} onChange={(v) => setForm({ ...form, about_body: v })} rows={5} />

            <h3 className="eyebrow pt-6">Socials & Contact</h3>
            <TextField testId="s-instagram" label="Instagram URL" value={form.socials.instagram} onChange={(v) => setSocials("instagram", v)} />
            <TextField testId="s-email" label="Email" value={form.socials.email} onChange={(v) => setSocials("email", v)} />
            <TextField testId="s-whatsapp" label="WhatsApp (digits w/ country code)" value={form.socials.whatsapp} onChange={(v) => setSocials("whatsapp", v)} placeholder="919999999999" />

            <h3 className="eyebrow pt-6">Locations served (comma separated)</h3>
            <TextField testId="s-locations" label="Locations" value={(form.locations_served || []).join(", ")} onChange={(v) => setForm({ ...form, locations_served: v.split(",").map((x) => x.trim()).filter(Boolean) })} />

            <TextField testId="s-footer-copy" label="Footer copyright text" value={form.footer_copy} onChange={(v) => setForm({ ...form, footer_copy: v })} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-12">
          <div>
            <h3 className="eyebrow mb-3">Colours</h3>
            {["background","surface","text_primary","text_secondary","accent"].map((k) => (
              <div key={k} className="flex items-center gap-3 mb-3">
                <input type="color" value={form.colors[k] || "#000000"} onChange={(e) => setColor(k, e.target.value)} className="w-10 h-10 bg-transparent border border-[color:var(--border)]" />
                <TextField testId={`s-color-${k}`} label={k.replace("_", " ")} value={form.colors[k]} onChange={(v) => setColor(k, v)} />
              </div>
            ))}
          </div>

          <div>
            <h3 className="eyebrow mb-3">Section Order & Visibility</h3>
            <div className="space-y-2">
              {form.section_order.map((k, idx) => (
                <div key={k} data-testid={`s-section-${k}`} className="flex items-center justify-between gap-3 border border-[color:var(--border)] px-3 py-2">
                  <div className="flex items-center gap-3">
                    <GripVertical size={14} className="text-[color:var(--text-muted)]" />
                    <div className="flex flex-col">
                      <button type="button" onClick={() => moveSection(idx, -1)} className="text-xs eyebrow link-underline">↑</button>
                      <button type="button" onClick={() => moveSection(idx, 1)} className="text-xs eyebrow link-underline">↓</button>
                    </div>
                    <span className="font-display italic text-xl">{SECTION_LABELS[k] || k}</span>
                  </div>
                  <Toggle testId={`s-visible-${k}`} label="Visible" value={form.section_visibility?.[k] !== false} onChange={(v) => setForm({ ...form, section_visibility: { ...form.section_visibility, [k]: v } })} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </AdminLayout>
  );
}
